import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { RegistryGlobalSummaryV2, FlexStringList } from '../../../types';
import { toStringList, hasValue } from './globalFormat';

interface GlobalTabV2Props {
  globalSummary: RegistryGlobalSummaryV2;
}

type FieldSpec = { field: string; labelKey: string };

function KvRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="registry-kv-row">
      <span className="registry-kv-key">{label}</span>
      <span className="registry-kv-value">{value}</span>
    </div>
  );
}

// 渲染对象里指定字段（仅有内容的字段）。无任何内容时返回空数组。
function objectRows(obj: unknown, fields: FieldSpec[], t: TFunction): ReactNode[] {
  if (!obj || typeof obj !== 'object') return [];
  const record = obj as Record<string, unknown>;
  return fields
    .filter(({ field }) => hasValue(record[field]))
    .map(({ field, labelKey }) => (
      <KvRow key={field} label={t(labelKey)} value={String(record[field])} />
    ));
}

// 把容错字符串数组渲染成 chips。
function ChipsRow({ label, value }: { label: string; value: FlexStringList | undefined }) {
  const items = toStringList(value);
  if (items.length === 0) return null;
  return (
    <div className="registry-kv-row">
      <span className="registry-kv-key">{label}</span>
      <span className="registry-kv-value registry-tag-row">
        {items.map((item, idx) => (
          <span key={idx} className="registry-tag">{item}</span>
        ))}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="registry-global-section">
      <div className="registry-global-section-title">{title}</div>
      <div className="registry-kv-list">{children}</div>
    </div>
  );
}

const BASIC_INFO_FIELDS: FieldSpec[] = [
  { field: 'drama_type', labelKey: 'global.v2.basicInfo.dramaType' },
  { field: 'one_sentence_type_summary', labelKey: 'global.v2.basicInfo.oneSentenceTypeSummary' },
  { field: 'core_hook', labelKey: 'global.v2.basicInfo.coreHook' },
];

const CORE_SETTING_FIELDS: FieldSpec[] = [
  { field: 'heroine_identity', labelKey: 'global.v2.coreSetting.heroineIdentity' },
  { field: 'heroine_initial_situation', labelKey: 'global.v2.coreSetting.heroineInitialSituation' },
  { field: 'hero_identity', labelKey: 'global.v2.coreSetting.heroIdentity' },
  { field: 'hero_hidden_identity', labelKey: 'global.v2.coreSetting.heroHiddenIdentity' },
  { field: 'core_misunderstanding', labelKey: 'global.v2.coreSetting.coreMisunderstanding' },
  { field: 'main_conflict', labelKey: 'global.v2.coreSetting.mainConflict' },
  { field: 'why_it_works', labelKey: 'global.v2.coreSetting.whyItWorks' },
];

const EMOTIONAL_STRUCTURE_FIELDS: FieldSpec[] = [
  { field: 'early_stage', labelKey: 'global.v2.emotionalStructure.earlyStage' },
  { field: 'middle_stage', labelKey: 'global.v2.emotionalStructure.middleStage' },
  { field: 'late_middle_stage', labelKey: 'global.v2.emotionalStructure.lateMiddleStage' },
  { field: 'final_stage', labelKey: 'global.v2.emotionalStructure.finalStage' },
  { field: 'overall_emotional_curve', labelKey: 'global.v2.emotionalStructure.overallEmotionalCurve' },
];

const MARKET_POSITIONING_FIELDS: FieldSpec[] = [
  { field: 'target_audience', labelKey: 'global.v2.marketPositioning.targetAudience' },
  { field: 'genre_appeal', labelKey: 'global.v2.marketPositioning.genreAppeal' },
  { field: 'content_style', labelKey: 'global.v2.marketPositioning.contentStyle' },
  { field: 'adaptation_or_promotion_value', labelKey: 'global.v2.marketPositioning.adaptationOrPromotionValue' },
];

// character_relationships 的各子块字段（标题 i18n key + 字段列表）。
const CHARACTER_BLOCKS: Array<{ block: string; titleKey: string; fields: FieldSpec[] }> = [
  {
    block: 'heroine',
    titleKey: 'global.v2.characters.heroine',
    fields: [
      { field: 'character_positioning', labelKey: 'global.v2.heroine.characterPositioning' },
      { field: 'external_conflict', labelKey: 'global.v2.heroine.externalConflict' },
      { field: 'emotional_motivation', labelKey: 'global.v2.heroine.emotionalMotivation' },
      { field: 'growth_arc', labelKey: 'global.v2.heroine.growthArc' },
    ],
  },
  {
    block: 'hero',
    titleKey: 'global.v2.characters.hero',
    fields: [
      { field: 'character_positioning', labelKey: 'global.v2.hero.characterPositioning' },
      { field: 'hidden_identity_logic', labelKey: 'global.v2.hero.hiddenIdentityLogic' },
      { field: 'love_expression', labelKey: 'global.v2.hero.loveExpression' },
      { field: 'pursuit_or_protection_logic', labelKey: 'global.v2.hero.pursuitOrProtectionLogic' },
    ],
  },
  {
    block: 'villain_or_scumbag',
    titleKey: 'global.v2.characters.villain',
    fields: [
      { field: 'character_positioning', labelKey: 'global.v2.villain.characterPositioning' },
      { field: 'conflict_function', labelKey: 'global.v2.villain.conflictFunction' },
      { field: 'face_slapping_function', labelKey: 'global.v2.villain.faceSlappingFunction' },
    ],
  },
  {
    block: 'female_rival_or_mistress',
    titleKey: 'global.v2.characters.rival',
    fields: [
      { field: 'character_positioning', labelKey: 'global.v2.rival.characterPositioning' },
      { field: 'conflict_function', labelKey: 'global.v2.rival.conflictFunction' },
      { field: 'emotion_function', labelKey: 'global.v2.rival.emotionFunction' },
    ],
  },
  {
    block: 'relationship_dynamic',
    titleKey: 'global.v2.characters.dynamic',
    fields: [
      { field: 'main_cp_dynamic', labelKey: 'global.v2.dynamic.mainCpDynamic' },
      { field: 'triangle_or_multi_relationship', labelKey: 'global.v2.dynamic.triangleOrMultiRelationship' },
      { field: 'tension_source', labelKey: 'global.v2.dynamic.tensionSource' },
    ],
  },
];

const SELLING_POINT_FIELDS: FieldSpec[] = [
  { field: 'selling_point', labelKey: 'global.v2.sellingPoint.sellingPoint' },
  { field: 'plot_expression', labelKey: 'global.v2.sellingPoint.plotExpression' },
  { field: 'why_it_works_for_short_drama', labelKey: 'global.v2.sellingPoint.whyItWorksForShortDrama' },
];

const VIRAL_ELEMENT_FIELDS: FieldSpec[] = [
  { field: 'element', labelKey: 'global.v2.viralElement.element' },
  { field: 'english_label', labelKey: 'global.v2.viralElement.englishLabel' },
  { field: 'plot_function', labelKey: 'global.v2.viralElement.plotFunction' },
];

// 新版（v2）世界观渲染：短剧项目分析风格的嵌套结构。
export default function GlobalTabV2({ globalSummary }: GlobalTabV2Props) {
  const { t } = useTranslation('scriptSummary');
  const g = globalSummary;

  const basicRows = objectRows(g.basic_info, BASIC_INFO_FIELDS, t);
  const coreRows = objectRows(g.core_setting, CORE_SETTING_FIELDS, t);
  const emotionRows = objectRows(g.emotional_structure, EMOTIONAL_STRUCTURE_FIELDS, t);
  const marketRows = objectRows(g.market_positioning, MARKET_POSITIONING_FIELDS, t);
  const stages = (g.plot_stages ?? []).filter(s => s && typeof s === 'object');
  const sellingPoints = (g.core_selling_points ?? []).filter(s => s && typeof s === 'object');
  const viralElements = (g.viral_elements ?? []).filter(v => v && typeof v === 'object');
  const relationships = g.character_relationships;

  return (
    <div className="registry-global-list registry-global-v2">
      {basicRows.length > 0 && <Section title={t('global.v2.sections.basicInfo')}>{basicRows}</Section>}

      {coreRows.length > 0 && <Section title={t('global.v2.sections.coreSetting')}>{coreRows}</Section>}

      {stages.length > 0 && (
        <div className="registry-global-section">
          <div className="registry-global-section-title">{t('global.v2.sections.plotStages')}</div>
          {stages.map((stage, idx) => {
            const label = hasValue(stage.stage_number)
              ? t('global.v2.plotStage.stageLabel', { num: stage.stage_number })
              : t('global.v2.plotStage.stageLabel', { num: idx + 1 });
            const title = hasValue(stage.stage_title) ? `${label} · ${stage.stage_title}` : label;
            return (
              <div key={idx} className="registry-global-subcard">
                <div className="registry-global-subcard-title">{title}</div>
                <div className="registry-kv-list">
                  {objectRows(stage, [
                    { field: 'stage_function', labelKey: 'global.v2.plotStage.stageFunction' },
                    { field: 'short_drama_trope', labelKey: 'global.v2.plotStage.shortDramaTrope' },
                  ], t)}
                  <ChipsRow label={t('global.v2.plotStage.keyEvents')} value={stage.key_events} />
                  <ChipsRow label={t('global.v2.plotStage.emotionPoints')} value={stage.emotion_points} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {relationships && (
        <div className="registry-global-section">
          <div className="registry-global-section-title">{t('global.v2.sections.characterRelationships')}</div>
          {CHARACTER_BLOCKS.map(({ block, titleKey, fields }) => {
            const obj = relationships[block as keyof typeof relationships] as Record<string, unknown> | undefined;
            const rows = objectRows(obj, fields, t);
            if (rows.length === 0) return null;
            return (
              <div key={block} className="registry-global-subcard">
                <div className="registry-global-subcard-title">{t(titleKey)}</div>
                <div className="registry-kv-list">{rows}</div>
              </div>
            );
          })}
        </div>
      )}

      {sellingPoints.length > 0 && (
        <div className="registry-global-section">
          <div className="registry-global-section-title">{t('global.v2.sections.sellingPoints')}</div>
          {sellingPoints.map((sp, idx) => {
            const rows = objectRows(sp, SELLING_POINT_FIELDS, t);
            if (rows.length === 0) return null;
            return (
              <div key={idx} className="registry-global-subcard">
                <div className="registry-kv-list">{rows}</div>
              </div>
            );
          })}
        </div>
      )}

      {emotionRows.length > 0 && (
        <Section title={t('global.v2.sections.emotionalStructure')}>{emotionRows}</Section>
      )}

      {viralElements.length > 0 && (
        <div className="registry-global-section">
          <div className="registry-global-section-title">{t('global.v2.sections.viralElements')}</div>
          {viralElements.map((ve, idx) => {
            const rows = objectRows(ve, VIRAL_ELEMENT_FIELDS, t);
            if (rows.length === 0) return null;
            return (
              <div key={idx} className="registry-global-subcard">
                <div className="registry-kv-list">{rows}</div>
              </div>
            );
          })}
        </div>
      )}

      {marketRows.length > 0 && (
        <Section title={t('global.v2.sections.marketPositioning')}>{marketRows}</Section>
      )}

      {hasValue(g.final_logline) && (
        <Section title={t('global.v2.sections.finalLogline')}>
          <div className="registry-kv-row">
            <span className="registry-kv-value">{String(g.final_logline)}</span>
          </div>
        </Section>
      )}
    </div>
  );
}
